import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { notification } from 'antd';

import ApiSitesContext from '@totem/components/administration/apiSites/ApiSitesContext';
import {
  ApiSite,
  ApiSiteStatus,
  ApiSiteTab,
} from '@totem/components/administration/apiSites/types';
import { getToken } from '@totem/components/userContext/accountUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { API_SITES_ENDPOINT } from '@totem/utilities/endpoints';

const EMPTY_ID = '000000000000000000000000';

type Props = {
  children: React.ReactNode;
};

const ApiSitesContainer = ({ children }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ApiSite[]>([]);
  const [activeTab, setActiveTab] = useState<ApiSiteTab>('current');
  const [editingSite, setEditingSite] = useState<ApiSite | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const currentOrgId = userProfile?.organization?.id ?? '';

  const refresh = useCallback(() => {
    setLoading(true);

    fetch(API_SITES_ENDPOINT, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((res) => res.json())
      .then((records: ApiSite[]) => {
        setData(records ?? []);
      })
      .catch(() => {
        notification.error({ message: 'Failed to load API sites.' });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredData = useMemo(
    () =>
      data.filter((site) => {
        if (activeTab === 'current')
          return site.organization?.id === currentOrgId;
        if (activeTab === 'unassigned')
          return site.organization?.id === EMPTY_ID;
        return true;
      }),
    [data, activeTab, currentOrgId],
  );

  const onEdit = (site: ApiSite) => {
    setEditingSite(site);

    setModalOpen(true);
  };

  const onModalClose = () => {
    setModalOpen(false);

    setEditingSite(null);
  };

  const onSave = (site: Partial<ApiSite>) => {
    fetch(`${API_SITES_ENDPOINT}/${site.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        type: site.type,
        displayName: site.displayName,
        organization: site.organization,
        region: site.region,
        building: site.building,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Save failed');
        notification.success({ message: 'API site updated.' });

        onModalClose();

        refresh();
      })
      .catch(() => {
        notification.error({ message: 'Failed to save API site.' });
      });
  };

  const onStatusChange = (id: string, status: ApiSiteStatus) => {
    fetch(`${API_SITES_ENDPOINT}/${id}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ status }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Status update failed');
        notification.success({ message: 'API site status updated.' });

        refresh();
      })
      .catch(() => {
        notification.error({ message: 'Failed to update API site status.' });
      });
  };

  const onTabChange = (tab: ApiSiteTab) => {
    setActiveTab(tab);
  };

  return (
    <ApiSitesContext.Provider
      value={{
        loading,
        data,
        filteredData,
        activeTab,
        editingSite,
        modalOpen,
        onRefresh: refresh,
        onEdit,
        onModalClose,
        onSave,
        onStatusChange,
        onTabChange,
      }}
    >
      {children}
    </ApiSitesContext.Provider>
  );
};

export default ApiSitesContainer;
